import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from '@/plugins/axios';
import i18next from '@/config/i18n';

// SYN-2738 (I6): o rótulo da faixa é do idioma da UI, não da config (que chega em
// pt-BR do backend). A faixa é identificada pela POSIÇÃO — são sempre três
// (`isValidTiersPayload`) — e o texto vem de `neuralTiers.{low,medium,high}`.
const NEURAL_TIER_LABEL_KEYS = ['low', 'medium', 'high'];
const withTierLabels = (tiers) => tiers.map((tier, i) => ({
    ...tier,
    label: i18next.t(`neuralTiers.${NEURAL_TIER_LABEL_KEYS[i]}`),
}));

export const useAppStore = defineStore('app', () => {
    const isInMaintenanceMode = ref(false);
    const maintenanceMessage = ref('O sistema está em manutenção. Por favor, tente mais tarde.'); // Default message
    const isCtrlPressed = ref(false); // Track Ctrl key press for super admin features
    const showSuperAdminFeatures = ref(false); // Show super admin features (persists after Ctrl release)
    let hideTimeout = null; // Timeout to hide features

    // Default fallback tiers in case API fails
    // Colors use accessible palette (teal for "Alto" instead of green)
    // to ensure deuteranopia/protanopia users can distinguish all zones
    const neuralTiers = ref(withTierLabels([
        { max: 70, color: '#C62828', icon: 'mdi-emoticon-sad-outline' },
        { max: 86, color: '#B36A00', icon: 'mdi-emoticon-neutral-outline' },
        { max: 100, color: '#0D9488', icon: 'mdi-emoticon-excited-outline' }
    ]));
    // Escada PRÓPRIA do vídeo (SPEC régua EN vídeo, 12/10): o vídeo é classificado contra
    // outros vídeos, com cortes servidos em /config/neural-tiers-video. Este default só vale se
    // essa rota falhar (ex.: front no ar antes do backend). Cortes 42/65 = P20/P80 do NE dos 2.447
    // filmes da base BRAIN da Forebrain (15/10); nunca herda a escada servida para imagem.
    const neuralTiersVideo = ref(withTierLabels([
        { max: 42, color: '#C62828', icon: 'mdi-emoticon-sad-outline' },
        { max: 65, color: '#B36A00', icon: 'mdi-emoticon-neutral-outline' },
        { max: 100, color: '#0D9488', icon: 'mdi-emoticon-excited-outline' }
    ]));
    i18next.on('languageChanged', () => {
        neuralTiers.value = withTierLabels(neuralTiers.value);
        neuralTiersVideo.value = withTierLabels(neuralTiersVideo.value);
    });
    // Escada do Neural Engagement para o tipo de mídia; qualquer coisa que não seja vídeo é imagem.
    const tiersFor = (mediaType) => (mediaType === 'video' ? neuralTiersVideo.value : neuralTiers.value);

    // Faixas do Gaze Distribution. Vinham CHUMBADAS no `GazeScoreCard.vue`, sem caminho nenhum
    // para recalibrar — para mudar 33/66 era preciso editar o arquivo e fazer deploy, enquanto o
    // EN já se editava pelo painel do superadmin. Estes valores reproduzem exatamente o que o
    // cartão desenhava, então servir a config não muda a tela; muda quem manda nela.
    //
    // Sem rótulo e sem ícone de propósito: o cartão do Gaze nunca exibiu nem um nem outro, e
    // inventá-los aqui mudaria a tela em nome de uniformidade com o EN.
    // Cortes ATUALIZADOS em 21/08, antes 33/66. O 33 punha 49,6% das peças em "baixo" e o 66
    // deixava só 0,4% em "alto" — seis em 1.660 —, então o verde era inalcançável, espelho do
    // vermelho inalcançável que o Engajamento Neural tinha. Os valores vêm do recorte de
    // calibração de `syn-ml/reports/censo_ne_gaze_2026-08-20.json`: união de representantes do
    // acervo com originais de cliente, n=1.660, convenção CAIXA (a que a produção conta hoje).
    // Arredondados para 18/44 em 28/10, quando a nota passou a sair inteira (`roundScore`).
    // Paleta preservada — o Rubens pediu em 18/08 para manter as cores de hoje.
    // Este é o DEFAULT; a config servida em /config/gaze-tiers tem precedência.
    const gazeTiers = ref([
        { max: 18, color: '#D81B60' },
        { max: 44, color: '#8E24AA' },
        { max: 100, color: '#2D5BFF' }
    ]);

    function setMaintenanceMode(status, message) {
        isInMaintenanceMode.value = status;
        if (message) {
            maintenanceMessage.value = message;
        }
    }

    function clearMaintenanceMode() {
        isInMaintenanceMode.value = false;
        // Optionally reset message to default or clear it
    }

    function triggerSuperAdminFeatures() {
        showSuperAdminFeatures.value = true;
        // Clear existing timeout
        if (hideTimeout) clearTimeout(hideTimeout);
        // Hide after 8 seconds of inactivity
        hideTimeout = setTimeout(() => {
            showSuperAdminFeatures.value = false;
        }, 8000);
    }

    // Frontend gauge code maps tiers by index → ['low', 'medium', 'high'] palette
    // keys, and renders an SVG arc segment per tier with length proportional to
    // (tier.max - prevTier.max). Any tier with non-ascending or non-positive max
    // produces a zero-length arc that visually drops a color from the gauge.
    // Reject any payload that wouldn't render three visible segments.
    //
    // 19/08 esta guarda foi afrouxada para aceitar corte 0 no primeiro tier, lendo o
    // pedido do Rubens como "nota 0 tem de cair no vermelho". 20/08 o Tomás decidiu o
    // contrário: o corte baixo volta a ser um valor alcançável (a recalibração 20/60/20
    // dá 70 — ver syn-ml/reports/faixas_en_conjunta_2026-08-20.json), porque com corte 0
    // NENHUMA peça real fica vermelha (o EN mínimo medido é 2,6 no acervo e 2,60 na
    // plataforma) e o medidor exibe duas cores em vez de três. Com o corte em 70 a guarda
    // não atrapalha, e a razão de restaurá-la é a próxima vez: config com corte 0 volta a
    // ser rejeitada como malformada em vez de aceita em silêncio.
    // A regra vale para os DOIS medidores: ambos desenham três segmentos e escolhem a cor pela
    // posição da faixa, então uma config que não renderiza três segmentos visíveis quebra os dois
    // do mesmo jeito. Uma função só, para as duas não divergirem com o tempo.
    function isValidTiersPayload(payload) {
        if (!Array.isArray(payload) || payload.length !== 3) return false;
        let prevMax = 0;
        for (const tier of payload) {
            const max = Number(tier?.max);
            if (!Number.isFinite(max) || max <= prevMax) return false;
            prevMax = max;
        }
        return true;
    }

    /**
     * Busca uma config de faixas e só adota se ela renderiza. Falha nunca derruba a tela: o
     * default local fica, porque medidor sem faixa nenhuma é pior que medidor com faixa velha.
     *
     * `_skipAuthRedirect` é cinto e suspensório. `App.vue` já chama isto atrás de
     * `isAuthenticated`, mas se algum chamador futuro disparar com token vencido não queremos
     * que uma config de aparência derrube a sessão inteira.
     */
    async function fetchTiers(path, destino, nome, adapt = (tiers) => tiers) {
        try {
            const response = await axios.get(path, { _skipAuthRedirect: true });
            if (isValidTiersPayload(response.data)) {
                destino.value = adapt(response.data);
            } else {
                console.warn(
                    `Ignoring invalid ${nome} tiers config from backend; keeping local 3-tier default.`,
                    response.data
                );
            }
        } catch (error) {
            console.error(`Failed to fetch ${nome} tiers config:`, error);
            // Keep default values
        }
    }

    const fetchNeuralTiers = () => fetchTiers('/config/neural-tiers', neuralTiers, 'neural', withTierLabels);
    const fetchNeuralTiersVideo = () => fetchTiers('/config/neural-tiers-video', neuralTiersVideo, 'neural video', withTierLabels);
    const fetchGazeTiers = () => fetchTiers('/config/gaze-tiers', gazeTiers, 'gaze');

    return {
        isInMaintenanceMode,
        maintenanceMessage,
        neuralTiers,
        neuralTiersVideo,
        tiersFor,
        gazeTiers,
        isCtrlPressed,
        showSuperAdminFeatures,
        setMaintenanceMode,
        clearMaintenanceMode,
        fetchNeuralTiers,
        fetchNeuralTiersVideo,
        fetchGazeTiers,
        triggerSuperAdminFeatures
    };
});
